import { FunctionComponent, useEffect, useMemo, useState } from 'react'
import {
  WButton,
  WChip,
  WLabel,
  WTextfield
} from '@alice-health/wonderland-react'
import { SlidePanel } from '@/global/components/SlidePanel'
import { useDrawer, usePreventPageScroll } from '@/global/hooks'
import {
  useFilters,
  useGetSubSpecialtiesFilter
} from '@/modules/accredited-establishments/hooks'
import { FilterItem } from '@/modules/accredited-establishments/api/types'
import { ALL_ID } from '@/modules/accredited-establishments/constants/filters'
import { ADDRESS_SEARCH_MODAL_ID } from '@/modules/accredited-establishments/components/Filters/AdressSearchModal/AddressSearchModal.constants'
import { DRAWER_CLASS_NAME } from '@/modules/accredited-establishments/components/Filters/Filters.constants'
import { FilterSection } from '@/modules/accredited-establishments/components/Filters/FilterSection'
import {
  ATTENDANCE_OPTIONS,
  GENDER_OPTIONS,
  getSpecialistChainState
} from '@/modules/accredited-establishments/components/Filters/SpecialistFilters'
import {
  toAccreditedGender,
  toAppointmentType
} from '@/modules/accredited-establishments/functions/parse-enum-param'
import { MOBILE_FILTERS_PANEL_ID } from './MobileFiltersPanel.constants'

/**
 * Mobile filters panel. Choices are a draft until `Aplicar`; the location is
 * the exception, since its own search panel writes on select.
 */
const MobileFiltersPanel: FunctionComponent = () => {
  const { isOpen, close } = useDrawer(MOBILE_FILTERS_PANEL_ID)
  const { open: openAddressSearch } = useDrawer(ADDRESS_SEARCH_MODAL_ID)

  const {
    plans,
    plan,
    specialties,
    specialty,
    subSpecialty,
    gender,
    appointmentType,
    address,
    categories,
    updatePlan,
    updateSpecialty,
    updateSubSpecialty,
    updateGender,
    updateAppointmentType
  } = useFilters()

  const [draftPlan, setDraftPlan] = useState<string>(ALL_ID)
  const [draftSpecialty, setDraftSpecialty] = useState<string>('')
  const [draftSubSpecialty, setDraftSubSpecialty] = useState<string>('')
  const [draftGender, setDraftGender] = useState<string>('')
  const [draftAppointmentType, setDraftAppointmentType] = useState<string>('')

  /* One section open at a time. */
  const [openedSection, setOpenedSection] = useState<string>('')

  usePreventPageScroll(isOpen)

  /* Reopening the panel drops the draft and shows the applied filters. */
  useEffect(() => {
    if (!isOpen) return

    setDraftPlan(plan?.value || ALL_ID)
    setDraftSpecialty(specialty?.value || '')
    setDraftSubSpecialty(subSpecialty?.value || '')
    setDraftGender(gender || '')
    setDraftAppointmentType(appointmentType || '')
    setOpenedSection('')
  }, [isOpen, plan, specialty, subSpecialty, gender, appointmentType])

  /* `ALL` is the empty state, not an option. */
  const planOptions = useMemo(
    () => (plans?.items || []).filter(({ value }) => value !== ALL_ID),
    [plans]
  )

  const specialtyOptions = useMemo(
    () => (specialties?.items || []).filter(({ value }) => value !== ALL_ID),
    [specialties]
  )

  const isSpecialistView = categories.includes('SPECIALIST')

  /* The draft drives the options, so the list follows the section above it. */
  const { data: subSpecialties } = useGetSubSpecialtiesFilter(draftSpecialty)

  const subSpecialtyOptions = useMemo(
    () => subSpecialties?.items || [],
    [subSpecialties]
  )

  const genderOptions = useMemo(
    () => GENDER_OPTIONS.map(({ label, value }) => ({ title: label, value })),
    []
  )

  const attendanceOptions = useMemo(
    () =>
      ATTENDANCE_OPTIONS.map(({ label, value }) => ({ title: label, value })),
    []
  )

  /* Same cascade as the desktop row. */
  const chain = getSpecialistChainState({
    specialty: draftSpecialty
  })

  const appliedPlan = plan?.value || ALL_ID
  const appliedSpecialty = specialty?.value || ''
  const appliedSubSpecialty = subSpecialty?.value || ''
  const appliedGender = gender || ''
  const appliedAppointmentType = appointmentType || ''

  const findOption = (options: FilterItem[], value: string) =>
    options.find(option => option.value === value)

  const onApply = () => {
    updatePlan(findOption(planOptions, draftPlan))

    if (isSpecialistView) {
      /* Order matters: each update clears the levels below it. */
      updateSpecialty(findOption(specialtyOptions, draftSpecialty))
      updateSubSpecialty(findOption(subSpecialtyOptions, draftSubSpecialty))
      updateGender(toAccreditedGender(draftGender))
      updateAppointmentType(toAppointmentType(draftAppointmentType))
    }

    close()
  }

  /* `Limpar` is not a draft: it clears the filters and closes the panel. */
  const onClear = () => {
    setDraftPlan(ALL_ID)
    setDraftSpecialty('')
    setDraftSubSpecialty('')
    setDraftGender('')
    setDraftAppointmentType('')

    updatePlan(undefined)

    if (isSpecialistView) {
      /* Clearing the specialty already clears the subspecialty. */
      updateSpecialty(undefined)
      /* Gender and appointment hang off neither, so each is cleared on its own. */
      updateGender(undefined)
      updateAppointmentType(undefined)
    }

    close()
  }

  /* Both footer buttons start disabled. */
  const hasAppliedFilter =
    appliedPlan !== ALL_ID ||
    (isSpecialistView &&
      (!!appliedSpecialty ||
        !!appliedSubSpecialty ||
        !!appliedGender ||
        !!appliedAppointmentType))

  const isUnchanged =
    draftPlan === appliedPlan &&
    (!isSpecialistView ||
      (draftSpecialty === appliedSpecialty &&
        draftSubSpecialty === appliedSubSpecialty &&
        draftGender === appliedGender &&
        draftAppointmentType === appliedAppointmentType))

  const toggleSection = (section: string) =>
    setOpenedSection(current => (current === section ? '' : section))

  const renderChips = (
    options: FilterItem[],
    selected: string,
    onSelect: (value: string) => void
  ) => (
    <div className='flex flex-wrap items-center gap-gl-spacing-01'>
      {options.map(({ title, value }) => (
        <WChip
          // `WChip` keeps its first render when `active` changes: remount it.
          key={`${value}-${value === selected}`}
          className='shrink-0'
          label={title}
          active={value === selected}
          onWClick={() => onSelect(value)}
        />
      ))}
    </div>
  )

  return (
    <SlidePanel
      id={MOBILE_FILTERS_PANEL_ID}
      data-testid='mobile-filters-panel'
      position='bottom'
      closeButton={false}
      className={DRAWER_CLASS_NAME}
    >
      <div className='flex flex-col h-full py-gl-spacing-08 px-gl-spacing-06 gap-gl-spacing-08'>
        <div className='flex items-center justify-between'>
          <WLabel size='large'>
            {isSpecialistView ? 'Filtrar especialistas' : 'Filtrar rede'}
          </WLabel>

          <WButton
            variant='tertiary'
            size='small'
            icon='icClose'
            iconButton
            onClick={close}
          />
        </div>

        {/* The field only shows the applied value and opens the search. */}
        <div onClick={openAddressSearch}>
          <WTextfield
            value={address?.title || ''}
            placeholder='Buscar nova localização'
            trailingIcon='icSearch'
            readOnly
          />
        </div>

        <div className='flex flex-col overflow-y-auto'>
          <FilterSection
            title='Tipo de plano'
            value={findOption(planOptions, draftPlan)?.title}
            opened={openedSection === 'plan'}
            onToggle={() => toggleSection('plan')}
          >
            {renderChips(planOptions, draftPlan, value =>
              setDraftPlan(current => (current === value ? ALL_ID : value))
            )}
          </FilterSection>

          {isSpecialistView ? (
            <>
              <FilterSection
                title='Especialidade'
                value={findOption(specialtyOptions, draftSpecialty)?.title}
                opened={openedSection === 'specialty'}
                onToggle={() => toggleSection('specialty')}
              >
                {renderChips(specialtyOptions, draftSpecialty, value =>
                  setDraftSpecialty(current => (current === value ? '' : value))
                )}
              </FilterSection>

              <FilterSection
                title='Subespecialidade'
                value={
                  findOption(subSpecialtyOptions, draftSubSpecialty)?.title
                }
                opened={openedSection === 'subSpecialty'}
                disabled={
                  chain.subSpecialty.disabled || !subSpecialtyOptions.length
                }
                onToggle={() => toggleSection('subSpecialty')}
              >
                {renderChips(subSpecialtyOptions, draftSubSpecialty, value =>
                  setDraftSubSpecialty(current =>
                    current === value ? '' : value
                  )
                )}
              </FilterSection>

              <FilterSection
                title='Gênero'
                value={findOption(genderOptions, draftGender)?.title}
                opened={openedSection === 'gender'}
                disabled={chain.gender.disabled}
                onToggle={() => toggleSection('gender')}
              >
                {renderChips(genderOptions, draftGender, value =>
                  setDraftGender(current => (current === value ? '' : value))
                )}
              </FilterSection>

              <FilterSection
                title='Atendimento'
                value={
                  findOption(attendanceOptions, draftAppointmentType)?.title
                }
                opened={openedSection === 'attendance'}
                disabled={chain.attendance.disabled}
                onToggle={() => toggleSection('attendance')}
              >
                {renderChips(attendanceOptions, draftAppointmentType, value =>
                  setDraftAppointmentType(current =>
                    current === value ? '' : value
                  )
                )}
              </FilterSection>
            </>
          ) : null}
        </div>

        <div className='mt-auto flex items-center gap-gl-spacing-04'>
          <WButton
            className='w-full *:w-full'
            variant='secondary'
            disabled={!hasAppliedFilter}
            onClick={onClear}
          >
            Limpar
          </WButton>

          <WButton
            className='w-full *:w-full'
            variant='cta'
            disabled={isUnchanged}
            onClick={onApply}
          >
            Aplicar
          </WButton>
        </div>
      </div>
    </SlidePanel>
  )
}

export { MobileFiltersPanel }
